import React, { useContext, useCallback, useMemo, useState } from "react";
import CancelButton from "../../buttons/CancelButton/CancelButton";
import Search from "../../UiLib/Search/Search";
import "./BoardToolbar.css";
import BtnDelete from "../../buttons/btnDelete.component/BtnDelete";
import BtnMove from "../../buttons/MoveButton/MoveButton";
import BtnSettings from "../../buttons/SettingsButton/SettingsButton";
import ActionModeButton from "../../buttons/BoardActionModeButton/ActionModeButton";
import BoardFastFilters from "../BoardFastFilters/BoardFastFilters";
import { AppContext } from "../../../App";
import { IFunctionPermissions } from "../../../common/api_interfaces/role/IFunctionPermissions";
import FilterContainer from "../../Shared/Filter/FilterContainer";
import { hasPermission } from "../../../helpers/hasPermisson";
import { BoardContext } from "../../../contexts/BoardContext";

export interface IToolbarProps {
    onMassActionsFunc: () => void;
    selectedCount: number;
    userAccess: IFunctionPermissions[];
}

const BoardToolbar: React.FC<IToolbarProps> = ({ onMassActionsFunc, selectedCount, userAccess }) => {
    const [showMoveDropdown, setShowMoveDropdown] = useState<boolean>(false);
    const { showToast } = useContext(AppContext);

    const { editBoard, delTask, editTask } = useMemo(() => ({
        editBoard: hasPermission(userAccess, "BoardAction", "edit"),
        delTask: hasPermission(userAccess, "TaskAction", "delete"),
        editTask: hasPermission(userAccess, "TaskAction", "edit")
    }), [userAccess]);

    const boardContext = useContext(BoardContext);

    const handleDeleteSubmit = useCallback(() => {
        if (delTask) {
            boardContext.handleDeleteTasks(boardContext.selectedTasks);
        } else {
            showToast("У вас нет прав для удаления задач(и)!");
        }
    }, [delTask, boardContext.handleDeleteTasks, boardContext.selectedTasks, showToast]);

    const toggleMoveDropdown = useCallback(() => {
        setShowMoveDropdown(prev => !prev);
    }, []);

    const closeMoveDropdown = useCallback(() => {
        setShowMoveDropdown(false);
    }, []);

    const handleColumnClick = useCallback((e: React.MouseEvent, columnId: number) => {
        boardContext.handleMoveTasks(columnId);
        closeMoveDropdown();
        e.stopPropagation();
    }, [boardContext.handleMoveTasks, closeMoveDropdown]);

    const handleBoardClick = useCallback((e: React.MouseEvent, boardId: number) => {
        boardContext.handleMoveTasksOutsideBoard(boardId);
        closeMoveDropdown();
        e.stopPropagation();
    }, [boardContext.handleMoveTasksOutsideBoard, closeMoveDropdown]);

    const onCancelClick = useCallback(() => {
        boardContext.resetFilter();
    }, [boardContext.resetFilter]);

    const handleSearchChange = useCallback((value: string) => {
        boardContext.setTaskNameSearch(value);
    }, [boardContext.setTaskNameSearch]);

    const moveDropdownContent = useMemo(() => {
        if (!showMoveDropdown) return null;

        return (
            <div className="move-task-popup-container">
                <div
                    className="move-task-popup-overlay"
                    onClick={closeMoveDropdown}
                ></div>
                <div className="move-task-popup">
                    <p className="move-task-popup-header">Переместить в колонку</p>
                    <div className="move-task-popup-column-container">
                        {boardContext.columns.map((c) => (
                            <p
                                key={c.id}
                                className="move-task-popup-column-text"
                                onClick={(e) => handleColumnClick(e, c.id)}
                            >
                                {c.name}
                            </p>
                        ))}
                    </div>
                    <p className="move-task-popup-header">Переместить на доску</p>
                    <div className="move-task-popup-column-container">
                        {boardContext.boardsForProxy.map((board) => (
                            <p
                                key={board.id}
                                className="move-task-popup-column-text"
                                onClick={(e) => handleBoardClick(e, board.id)}
                            >
                                {board.name}
                            </p>
                        ))}
                    </div>
                </div>
            </div>
        );
    }, [
        showMoveDropdown, 
        boardContext.columns, 
        boardContext.boardsForProxy, 
        handleColumnClick, 
        handleBoardClick, 
        closeMoveDropdown
    ]);

    const massActionsContent = useMemo(() => {
        if (!boardContext.actionMode || selectedCount <= 0) return null;

        return (
            <div className="mass-actions">
                <BtnMove onClickFunc={toggleMoveDropdown} permission={editTask} />
                {moveDropdownContent}
                <BtnDelete onClickFunc={handleDeleteSubmit} permission={delTask} />
            </div>
        );
    }, [boardContext.actionMode, selectedCount, editTask, moveDropdownContent, handleDeleteSubmit, delTask, toggleMoveDropdown]);

    const selectedCountDisplay = useMemo(() => {
        if (!boardContext.actionMode) return null;
        return (
            <span className="toolbar__point--task__counter">
                {selectedCount > 0 ? selectedCount : 0}
            </span>
        );
    }, [boardContext.actionMode, selectedCount]);

    return (
        <div className="board-toolbar">
            <div className="toolbar-left">
                {boardContext.boardFilters && (
                    <BoardFastFilters
                        taskFilters={boardContext.boardFilters}
                        changeFastFilters={boardContext.changeFastFilters}
                        selectedFastFilters={boardContext.selectedFastFilters}
                    />
                )}
            </div>
            <div className="toolbar-right" style={{ alignSelf: "end" }}>
                {boardContext.actionMode && <CancelButton onClickFunc={boardContext.cancelActionMode} />}
                {massActionsContent}
                {selectedCountDisplay}
                <ActionModeButton 
                    onClickFunc={onMassActionsFunc} 
                    isActive={boardContext.actionMode} 
                    selectedCount={selectedCount} 
                />
                <Search 
                    value={boardContext.taskNameSearch} 
                    onSubmitFunc={undefined} 
                    changeSearchValue={handleSearchChange} 
                />
                <FilterContainer
                    filterCounter={boardContext.filterCounter}
                    setFilterCounter={boardContext.setFilterCounter}
                    selectedOptions={boardContext.selectedBoardFilters}
                    setSelectedOptions={boardContext.setSelectedBoardFilters}
                    filterOptions={boardContext.boardFilters}
                    showResetButton={boardContext.taskNameSearch.length > 0}
                    onReset={onCancelClick}
                />
                <BtnSettings onClickFunc={boardContext.openMenu} permission={editBoard} />
            </div>
        </div>
    );
};

export default React.memo(BoardToolbar);